import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import {
  plan_top_down_by_channel_path,
  plan_top_down_by_channel_quarterly_path,
  plan_top_down_path
} from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

type StepKind = 'overall' | 'channel' | 'quarterly'

interface Props {
  slug: string
  currentStep: StepKind
  overallCompletedAt: string | null
  channelCompletedAt: string | null
  quarterlyCompletedAt?: string | null
  onCompleteOverall: () => void
  onCompleteChannel?: () => void
  onCompleteQuarterly?: () => void
  channelStatusLabel?: string
  quarterlyStatusLabel?: string
}

interface StepConfig {
  num: number
  kind: StepKind
  title: string
  description: string
}

const STEPS: StepConfig[] = [
  { num: 1, kind: 'overall', title: 'Overall Plan', description: 'Set high-level revenue targets' },
  { num: 2, kind: 'channel', title: 'Channel Breakdown', description: 'Revenue targets breakdown by channel' },
  { num: 3, kind: 'quarterly', title: 'Channel Quarterly Breakdown', description: 'Channel revenue targets quarterly breakdown' }
]

function formatCompletedAt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function StepProgress({
  slug,
  currentStep,
  overallCompletedAt,
  channelCompletedAt,
  quarterlyCompletedAt = null,
  onCompleteOverall,
  onCompleteChannel,
  onCompleteQuarterly,
  channelStatusLabel,
  quarterlyStatusLabel
}: Props) {
  const market = useMarket()
  const overallDone = !!overallCompletedAt
  const channelDone = !!channelCompletedAt
  const quarterlyDone = !!quarterlyCompletedAt

  return (
    <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
      {STEPS.map((step) => {
        const isCurrent = step.kind === currentStep
        const done =
          (step.kind === 'overall' && overallDone) ||
          (step.kind === 'channel' && channelDone) ||
          (step.kind === 'quarterly' && quarterlyDone)
        const locked =
          (step.kind === 'channel' && !overallDone) ||
          (step.kind === 'quarterly' && !channelDone)

        return (
          <div
            key={step.num}
            className="border rounded-lg p-4 flex flex-col gap-2"
            style={{
              background: 'var(--background)',
              borderColor: isCurrent ? 'var(--primary)' : 'var(--border)',
              opacity: locked ? 0.55 : 1
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center text-xs font-semibold rounded-full"
                style={{
                  width: 22,
                  height: 22,
                  background: 'var(--primary)',
                  color: 'var(--primary-foreground)'
                }}
              >
                {done ? '✓' : step.num}
              </span>
              <div style={{ color: 'var(--foreground)' }} className="text-sm font-semibold">
                {step.title}
              </div>
            </div>
            <div style={{ color: 'var(--muted-foreground)' }} className="text-xs">
              {step.description}
            </div>
            <div className="mt-1">
              {renderAction({
                step,
                isCurrent,
                done,
                locked,
                slug,
                marketSlug: market.slug,
                overallCompletedAt,
                channelCompletedAt,
                quarterlyCompletedAt,
                onCompleteOverall,
                onCompleteChannel,
                onCompleteQuarterly,
                channelStatusLabel,
                quarterlyStatusLabel
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function renderAction({
  step,
  isCurrent,
  done,
  locked,
  slug,
  marketSlug,
  overallCompletedAt,
  channelCompletedAt,
  quarterlyCompletedAt,
  onCompleteOverall,
  onCompleteChannel,
  onCompleteQuarterly,
  channelStatusLabel,
  quarterlyStatusLabel
}: {
  step: StepConfig
  isCurrent: boolean
  done: boolean
  locked: boolean
  slug: string
  marketSlug: string
  overallCompletedAt: string | null
  channelCompletedAt: string | null
  quarterlyCompletedAt: string | null
  onCompleteOverall: () => void
  onCompleteChannel?: () => void
  onCompleteQuarterly?: () => void
  channelStatusLabel?: string
  quarterlyStatusLabel?: string
}) {
  if (locked) {
    const blockingStep = step.kind === 'quarterly' ? 2 : 1
    return (
      <div style={{ color: 'var(--muted-foreground)' }} className="text-xs italic">
        Locked — complete step {blockingStep}
      </div>
    )
  }

  if (step.kind === 'overall') {
    if (done) {
      return (
        <div className="flex flex-col gap-1">
          <div style={{ color: 'var(--muted-foreground)' }} className="text-xs">
            Completed {formatCompletedAt(overallCompletedAt!)}
          </div>
          {!isCurrent && (
            <Link
              href={plan_top_down_path(marketSlug, slug)}
              className="text-xs underline hover:no-underline self-start"
              style={{ color: 'var(--link)' }}
            >
              Go to Overall Plan
            </Link>
          )}
        </div>
      )
    }
    return (
      <Button size="sm" onClick={onCompleteOverall}>
        Complete
      </Button>
    )
  }

  if (step.kind === 'channel') {
    if (done) {
      return (
        <div className="flex flex-col gap-1">
          <div style={{ color: 'var(--muted-foreground)' }} className="text-xs">
            Completed {formatCompletedAt(channelCompletedAt!)}
          </div>
          {!isCurrent && (
            <Link
              href={plan_top_down_by_channel_path(marketSlug, slug)}
              className="text-xs underline hover:no-underline self-start"
              style={{ color: 'var(--link)' }}
            >
              Go to Channel Breakdown
            </Link>
          )}
        </div>
      )
    }
    if (isCurrent) {
      return (
        <div className="flex flex-col gap-1 items-start">
          <Button size="sm" onClick={onCompleteChannel}>
            Complete
          </Button>
          {channelStatusLabel && (
            <div className="text-xs text-muted-foreground">{channelStatusLabel}</div>
          )}
        </div>
      )
    }
    return (
      <Link
        href={plan_top_down_by_channel_path(marketSlug, slug)}
        className="text-xs underline hover:no-underline"
        style={{ color: 'var(--link)' }}
      >
        Go to Channel Breakdown
      </Link>
    )
  }

  // step.kind === 'quarterly'
  if (done) {
    return (
      <div className="flex flex-col gap-1">
        <div style={{ color: 'var(--muted-foreground)' }} className="text-xs">
          Completed {formatCompletedAt(quarterlyCompletedAt!)}
        </div>
        {!isCurrent && (
          <Link
            href={plan_top_down_by_channel_quarterly_path(marketSlug, slug)}
            className="text-xs underline hover:no-underline self-start"
            style={{ color: 'var(--link)' }}
          >
            Go to Channel Quarterly Breakdown
          </Link>
        )}
      </div>
    )
  }
  if (isCurrent) {
    return (
      <div className="flex flex-col gap-1 items-start">
        <Button size="sm" onClick={onCompleteQuarterly}>
          Complete
        </Button>
        {quarterlyStatusLabel && (
          <div className="text-xs text-muted-foreground">{quarterlyStatusLabel}</div>
        )}
      </div>
    )
  }
  return (
    <Link
      href={plan_top_down_by_channel_quarterly_path(marketSlug, slug)}
      className="text-xs underline hover:no-underline"
      style={{ color: 'var(--link)' }}
    >
      Go to Channel Quarterly Breakdown
    </Link>
  )
}
